import React from 'react';
import styled from 'styled-components';
import { Row, Col } from 'antd';
import { NestedCard } from './LiquidGlass/index';
import DynamicPageWrapper from './DynamicPageWrapper';
import { useResponsiveLayout } from '../config/componentLayout';

// DWEB-213: About renders plain elements, not antd Typography. Typography reached the
// eager path only through this component, Footer and ConsentBanner, and its Base
// imports Tooltip (@rc-component/trigger, resize-observer-polyfill, …), Editable (the
// input/TextArea stack) and the en_US locale bundle, none of which this site renders:
// 111 modules left main.js in the trace. The tags stay what Typography emitted (h2 for
// Title level 2, div for Paragraph), so heading structure and tag-keyed CSS are
// unchanged. The styles Typography supplied that nothing else does are restated from
// antd 5.27.4's source, and the before/after computed-style diff is empty:
//   root: line-height = token lineHeight, UNITLESS (a px value would change inline
//         children's line boxes); word-break: break-word
//   strong: font-weight = token fontWeightStrong (600, not the browser's 700)
//   a: user-select: text (Typography's link rule; the colour is the anchor's own inline style)
//   a:focus-visible: the KEYBOARD FOCUS RING — 3px solid #91caff, offset 1px (getLinkStyles).
//      🔴 The first cut omitted it and the LinkedIn link lost its focus ring entirely: the
//      element-level style diff was empty because a ring exists only in a forced state.
//      Found by the v2 capture with :hover/:focus/:focus-visible/:active forced over CDP;
//      typography-eager-path.spec.js now holds it.
//   a:hover / a:focus / a:active: outline none, as Typography's operationUnit had it.
// data-typo marks the replaced elements for that comparison and for the e2e selectors.
const Para = styled.div.attrs({ 'data-typo': '' })`
  line-height: 1.5714285714285714;
  word-break: break-word;

  strong {
    font-weight: 600;
  }

  a {
    user-select: text;
  }

  a:hover,
  a:focus,
  a:active {
    outline: none;
  }

  a:focus-visible {
    outline: 3px solid #91caff;
    outline-offset: 1px;
    transition: outline-offset 0s, outline 0s;
  }
`;

/**
 * Copy is WEBSITE_COPY_DRAFT.md sections 2 and 5, verbatim (DWEB-48, approved
 * 2026-08-24). It replaces the 2024 "serious technical expertise should be
 * within reach of anyone with a hard problem" framing, which read as a
 * general-purpose consultancy and contradicted the leadership-first offers the
 * Services section now leads with.
 */
/**
 * DWEB-177 (v6.9.0) — the practice is one person's credibility, and the page
 * showed neither a face nor a link.
 *
 * THESE THREE VALUES HAVE A SECOND HOME IN public/index.html, which is static
 * and cannot import them: the Person node's `image` and `sameAs` say the same
 * thing to a crawler that this section says to a reader. index.html cannot be
 * made to read this module, so the two are held equal by a TEST instead —
 * e2e/about-portrait.spec.js — which is the DWEB-48 pattern already used for
 * the FAQ and the offer catalogue. Change one and the gate names the other.
 *
 * `rel="me"` is the point of the link, not `nofollow` or `noopener`: it is the
 * attribute that asserts IDENTITY — same person — rather than endorsement.
 * `noopener noreferrer` rides along because it is the site's pattern for every
 * external link and what eslint's jsx-no-target-blank requires.
 */
const LINKEDIN_URL = 'https://www.linkedin.com/in/paravindranath';

/**
 * A cutout with a real alpha channel, not a photo on a white square: 52.8%
 * fully opaque, 45.6% fully transparent, and the cut edge measures mean
 * luminance 57 with 1.4% of edge pixels brighter than 200 — so it composites
 * onto the black ground DWEB-170 established without a grey halo. WebP at
 * q82 with `-alpha_q 100`, which encodes the ALPHA losslessly: the 4,201
 * semi-transparent edge pixels are bit-identical to the 510 KB source PNG,
 * and only RGB is lossy. That is what keeps the matte from coming back.
 */
const PORTRAIT = {
  src: '/images/pradeep-ravindranath-608.webp',
  srcSet:
    '/images/pradeep-ravindranath-304.webp 304w, ' +
    '/images/pradeep-ravindranath-608.webp 608w',
  // Rendered at ~190px (140px on phones), so 304w covers 2x on a phone and
  // 608w covers 3.2x on desktop.
  sizes: '(max-width: 991px) 170px, 240px',
  width: 608,
  height: 788,
};

/**
 * DWEB-177 — the portrait beside the copy, not above it.
 *
 * Side by side needs LEFT-aligned body text: a centred ragged column next to an
 * image reads as two unrelated blocks. So the alignment is part of the layout,
 * not a separate decision — and it reverts to centred when the two stack.
 *
 * Breakpoint 992px rather than the site's usual 768: at 768-991 the copy column
 * is too narrow beside a 240px portrait and the paragraphs go to 3-4 words a
 * line. Below 992 it stacks and behaves exactly as the centred version did.
 */
const Split = styled.div`
  display: flex;
  align-items: center;
  gap: 2.75rem;
  margin-bottom: 1.5rem;

  @media (max-width: 991px) {
    flex-direction: column;
    gap: 1.5rem;
  }
`;

const PortraitSlot = styled.div`
  flex: 0 0 auto;
  display: flex;
  justify-content: center;
`;

const CopySlot = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  text-align: left;

  @media (max-width: 991px) {
    text-align: center;
  }
`;

const CARDS = [
  {
    title: 'Our Mission',
    body:
      'Domain visions, shipped as production systems — with neither the domain nor the ' +
      'engineering compromised.',
  },
  {
    title: 'Our Vision',
    body:
      'Products that outlive their launch: scalable, reliable, regulation-ready, and ' +
      'maintainable from the first design decision.',
  },
  {
    title: 'Our Values',
    body: 'Honesty, rigor, and pragmatism.',
  },
];

function About() {
  const layout = useResponsiveLayout('about');

  const bodyStyle = {
    fontSize: '1.1rem',
    // Alignment comes from CopySlot: left beside the portrait, centred when stacked.
    marginBottom: '1.5rem',
    color: '#e5e7eb',
    textShadow: '0 2px 4px rgba(0, 0, 0, 0.8), 0 1px 2px rgba(0, 0, 0, 0.6)',
  };

  return (
    <DynamicPageWrapper
      id="about"
      componentName="about"
      backgroundKey="about-section"
    >
      <Row justify="center">
        <Col span={20}>
          <h2
            data-typo=""
            style={{
              wordBreak: 'break-word',
              textAlign: 'center',
              marginBottom: layout.titleMargin,
              color: '#ffffff',
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.8), 0 1px 2px rgba(0, 0, 0, 0.6)'
            }}
          >
            One expert, fluent in both languages
          </h2>

          {/* Unframed, directly on the About card. The cutout's own background
              is transparent and the card behind it IS the frame; wrapping it in
              a second glass surface would make a card inside a card — which is
              the nesting that made the hero read as a hole before DWEB-176.
              A portrait is content, not a surface, so it gets no rim. */}
          <Split>
            <PortraitSlot>
            <img
              src={PORTRAIT.src}
              srcSet={PORTRAIT.srcSet}
              sizes={PORTRAIT.sizes}
              width={PORTRAIT.width}
              height={PORTRAIT.height}
              alt="Pradeep Ravindranath"
              loading="lazy"
              decoding="async"
              style={{
                width: 'clamp(170px, 24vw, 240px)',
                height: 'auto',
                display: 'block',
                // Grounds the cutout so it does not read as an escaped asset,
                // without adding a frame. Neutral, so no colour is doing work.
                //
                // 🔴 DO NOT ADD A `mask-image` BOTTOM FADE TO SOFTEN THE CUT.
                // Tried and rejected on a built page: `mask` applies AFTER
                // `filter`, so the mask clips the drop-shadow — a shadow that
                // followed the body's alpha becomes a visible RECTANGLE around
                // the portrait, which is worse than the hard edge it was meant
                // to fix. Fade the shadow or the image, not both.
                filter: 'drop-shadow(0 18px 32px rgba(0, 0, 0, 0.55))',
              }}
            />
            </PortraitSlot>

            <CopySlot>
          <Para style={bodyStyle}>
            DynamWorks is the consulting practice of{' '}
            <a
              href={LINKEDIN_URL}
              target="_blank"
              rel="me noopener noreferrer"
              title="Pradeep Ravindranath on LinkedIn"
              style={{ color: 'inherit', textDecoration: 'underline', textDecorationColor: '#F59E0B', textUnderlineOffset: '3px' }}
            >
              <strong style={{ color: '#F59E0B' }}>Pradeep Ravindranath</strong>
            </a>{' '}
            — PhD, MBA, and nearly two decades of carrying products from idea to production across healthcare, tax, automotive, and research.
          </Para>

          <Para style={bodyStyle}>
            The technical part — architecture, scale, reliability — any experienced leader can do. The rare work is what happens <em>before and around</em> it: <strong>intertwining a domain's requirements with the technical design.</strong> A tax professional, a clinician, a founder-engineer each hold a vision of their product that no spec captures. Translating that vision into technical reality — without flattening the domain to fit a stack, and without letting domain enthusiasm compromise the engineering — is a battle between R&amp;D and production. That battle is my work.
          </Para>

          <Para style={{ ...bodyStyle, marginBottom: 0 }}>
            The standard isn't claimed, it's shipped: App Store products built and maintained by one person, interfaces that work with VoiceOver and at the largest text sizes, privacy architectures with no account and no cloud — and the AI receptionist answering the phone line and this page from one knowledge base.
          </Para>
            </CopySlot>
          </Split>

          <Row gutter={layout.gutter} justify="center">
            {CARDS.map((card) => (
              <Col {...layout.columnConfig} key={card.title}>
                <NestedCard variant="default" glassVariant="auto" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <h3 style={{ fontSize: layout.isMobile ? '1.2rem' : '1.3rem', marginBottom: layout.isMobile ? '0.75rem' : '1.5rem', textAlign: 'center', color: '#F59E0B', textShadow: '0 2px 4px rgba(0, 0, 0, 0.8), 0 1px 2px rgba(0, 0, 0, 0.6)' }}>{card.title}</h3>
                  <p style={{ flex: 1, fontSize: layout.isMobile ? '0.95rem' : '1rem', lineHeight: layout.isMobile ? '1.6' : '1.7', textAlign: 'center', color: '#d1d5db', textShadow: '0 2px 4px rgba(0, 0, 0, 0.8), 0 1px 2px rgba(0, 0, 0, 0.6)' }}>{card.body}</p>
                </NestedCard>
              </Col>
            ))}
          </Row>

          {/* Anti-positioning (draft section 5). The draft allows "after Services
              or in About"; it sits here so About answers "who is this" and "who
              is this NOT" in one place, and so no new top-level section — and
              therefore no new nav item — is introduced for four sentences. */}
          <NestedCard
            variant="default"
            glassVariant="auto"
            style={{ marginTop: layout.paragraphMargin, borderLeft: '4px solid #F59E0B' }}
          >
            <p style={{
              margin: 0,
              fontSize: layout.isMobile ? '0.95rem' : '1.05rem',
              lineHeight: layout.isMobile ? '1.6' : '1.8',
              color: '#e5e7eb',
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.8), 0 1px 2px rgba(0, 0, 0, 0.6)',
            }}>
              <strong style={{ color: '#F59E0B' }}>What this isn't.</strong> Not an agency — nothing is handed off to a rotating team. Not staff augmentation — you buy judgment and outcomes, not hours. Not a demo shop — production is the deliverable. And not a technologist who flattens your domain to fit a stack: the domain's truth and the engineering's integrity both survive, or it doesn't ship.
            </p>
          </NestedCard>
        </Col>
      </Row>
    </DynamicPageWrapper>
  );
}

export default About;
